import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { BehaviorSubject, Subject, Subscription, throwError } from 'rxjs';
import { User } from './user.model';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { Signup } from './signup/signup.model';
import { Activation } from './activation/activation.model';
import { FormGroup } from '@angular/forms';
import { Reset } from './reset-password/reset-password.model';
import { ResetRequest } from './reset-request/reset-request.model';
import { Role } from './role.model';
import { LogService } from 'src/app/shared/services/log.service';
import { GlobalService } from 'src/app/shared/services/global.service';
import { env } from 'process';
import { IdentityService } from 'src/app/shared/services/identity.service';
import { UserId } from 'src/app/shared/profile/profile/profile.model';
import { MemberSearch } from 'src/app/shared/member-search/member-search.model';
import { AppService } from 'src/app/shared/services/app.service';
var aesjs = require('aes-js');

const key = [210, 21, 32, 4, 45, 14, 27, 8, 9, 11, 111, 12, 13, 14, 35, 16];

export interface loginResponse {
  email: string;
  token: string;
  id: string;
  memberId: number;
  isLeader: boolean;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  roles: string[];
  expiration: Date;
  language: string;
}

export interface UserData {
  email: string;
  id: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  roles: string[];
  _token: string;
  _tokenExpiration: Date;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  pipe(arg0: any) {
    throw new Error('Method not implemented.');
  }
  user = new BehaviorSubject<User>(null);
  apiHost: string = environment.apiHost;
  userSub: Subscription;
  userData: UserData;
  private tokenExpirationTimer: any;

  constructor(
    private http: HttpClient,
    private router: Router,
    private appService: AppService,
    private globalService: GlobalService,
    public identityService: IdentityService
  ) { }

  UserData: {
    email: string;
    id: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    roles: string[];
    _token: string;
    _tokenExpiration: Date;
  };

  Initialize() {
    if (this.user.value == null) this.autoLogin(1);
  }

  getRolesByUserId(userId: string) {
    var link = `${this.apiHost}/Permission/GetRoles?UserId=`;
    let promise = new Promise<Role[]>((resolve, reject) => {
      let apiURL = link + userId;
      this.http
        .get<Role[]>(apiURL)
        .toPromise()
        .then(
          (res) => {
            resolve(res);
          },
          (msg) => {
            resolve(null);
          }
        );
    });
    return promise;
  }

  getMemberSearchbyId(memberId: number) {
    var link = `${this.apiHost}/Member/getMemberSearchById?MemberId=` + memberId;
    let promise = new Promise<MemberSearch>((resolve, reject) => {
      let apiURL = link;
      this.http
        .get<MemberSearch>(apiURL)
        .toPromise()
        .then(
          (res) => {
            resolve(res);
          },
          (msg) => {
            resolve(null);
          }
        );
    });
    return promise;
  }

  getUserById(myId: UserId) {
    var link = `${this.apiHost}/User/getUser`;
    let promise = new Promise<User>((resolve, reject) => {
      let apiURL = link;
      this.http
        .post<User>(apiURL, myId)
        .toPromise()
        .then(
          (res) => {
            resolve(res);
          },
          (msg) => {
            resolve(null);
          }
        );
    });
    return promise;
  }

  login(
    email: string,
    password: string,
    proceed: boolean,
    setAsMembers: number
  ): Promise<any> {
    var option =
      '?proceed=' +
      proceed.toString() +
      '&setAsMembers=' +
      setAsMembers.toString();
    let promise = new Promise<any>((resolve, reject) => {
      return this.http
        .post<any>(this.apiHost + '/Authenticate/Login' + option, {
          email: email,
          password: password,
        })
        .toPromise()
        .then(
          (res) => { 
            this.handleAuthentication(
              res.email,
              res.id,
              res.memberId,
              res.isLeader,
              res.firstName,
              res.lastName,
              res.phoneNumber,
              res.roles,
              res.token,
              res.expiration,
              res.language
            );
            resolve(res);
          },  
          (msg) => {
            resolve(null);
          }
        );
    });
    return promise; 
  }   


  signup(signupData: Signup): Promise<any> {
    let promise = new Promise<any>((resolve, reject) => {
      return this.http
        .post<any>(this.apiHost + '/Authenticate/Signup/', signupData)
        .toPromise()
        .then(
          (res) => {
            resolve(res);
          },
          (msg) => {
            resolve(null);
          }
        );
    });
    return promise; 
  }

  encrypt(userData: User): string {
    try {
      if (userData == null)
        return null;
      var textBytes = aesjs.utils.utf8.toBytes(JSON.stringify(userData));
      var aesCtr = new aesjs.ModeOfOperation.ctr(key, new aesjs.Counter(5));
      var encryptedBytes = aesCtr.encrypt(textBytes);
      var encryptedHex = aesjs.utils.hex.fromBytes(encryptedBytes);
      return encryptedHex;
    }
    catch {
      return null;
    }
  }

  decrypt(encryptedHex: string): any {
    try {
      if (encryptedHex == null)
        return null;
      var encryptedBytes = aesjs.utils.hex.toBytes(encryptedHex);
      var aesCtr = new aesjs.ModeOfOperation.ctr(key, new aesjs.Counter(5));
      var decryptedBytes = aesCtr.decrypt(encryptedBytes);
      var decryptedText = aesjs.utils.utf8.fromBytes(decryptedBytes);
      return JSON.parse(decryptedText);
    }
    catch {
      return null;
    }
  }

  requestReset(email: ResetRequest) {
    return this.http
      .post<ResetRequest>(
        this.apiHost + '/Authenticate/ResetPasswordRequest/',
        email
      )
      .pipe(catchError(this.handleError), tap());
  }

  getPhoneResetToken(email: ResetRequest) {
    return this.http
      .post<ResetRequest>(
        this.apiHost + '/Authenticate/GetPhoneResetToken/',
        email
      )
      .pipe(catchError(this.handleError), tap());
  }

  ResetPassword(resetData: Reset) {
    return this.http
      .post<Reset>(this.apiHost + '/Authenticate/ResetPassword/', resetData)
      .pipe(catchError(this.handleError), tap());
  }

  validate(activationDate: Activation) {
    return this.http
      .post<string>(this.apiHost + '/Authenticate/Validate/', activationDate)
      .pipe(catchError(this.handleError), tap());
  }

  logout(mode: number) {
    this.user.next(null);
    localStorage.removeItem('MemberId');
    localStorage.removeItem('isLeader');
    localStorage.removeItem('userData');
    localStorage.removeItem('consent');
    if (this.tokenExpirationTimer) {
      clearTimeout(this.tokenExpirationTimer);
    }
    if (mode == 1)
      this.router.navigate([this.globalService.subdomain + '/login']);
  }

  isOutdated(token: string): boolean {
    if (JSON.parse(atob(token.split('.')[1])).aud == 'User') return true;
    return false;
  }

  async autoLoginIfExist(mode: number): Promise<User> {
    const userData: {
      email: string;
      id: string;
      firstName: string;
      lastName: string;
      phoneNumber: string;
      roles: string[];
      memberId: number;
      isLeader: boolean;
      _token: string;
      _tokenExpiration: Date;
      language: string;
    } = this.decrypt(localStorage.getItem('userData'));
    var memberId = localStorage.getItem('memberId');  // check if old properties exist then signout
    var isLeader = localStorage.getItem('isLeader');

    if (userData.language == '')
      userData.language = 'English';

    const loadedUser = new User(
      userData.email,
      userData.id,
      userData.memberId,
      userData.isLeader,
      userData.firstName,
      userData.lastName,
      userData.phoneNumber,
      userData.roles,
      userData._token,
      userData._tokenExpiration,
      userData.language
    );
    this.userData = userData;
    await this.identityService.loadUser(loadedUser);
    this.globalService.currentUser = userData.email;
    this.globalService.currentUserId = userData.id;
    this.globalService.currentUserName =
      userData.firstName + ' ' + userData.lastName;
    this.globalService.currentFirstName = userData.firstName;
    if (loadedUser.token) this.user.next(loadedUser);
    const expirationDuration =
      new Date(userData._tokenExpiration).getTime() - new Date().getTime();
    this.autoLogout(expirationDuration, mode);
    return loadedUser;
  }

  async silentLogin(mode: number): Promise<User> {
    const userData: {
      email: string;
      id: string;
      firstName: string;
      lastName: string;
      phoneNumber: string;
      roles: string[];
      memberId: number;
      isLeader: boolean;
      _token: string;
      _tokenExpiration: Date;
      language: string;
    } = this.decrypt(localStorage.getItem('userData'));
    var memberId = localStorage.getItem('memberId');  // check if old properties exist then signout
    var isLeader = localStorage.getItem('isLeader');
    if (memberId != null || isLeader != null) {
      return null;
    }
    if (!userData || (userData.isLeader === undefined) || (userData.memberId === undefined) || this.isOutdated(userData._token)) {
      return null;
    }

    if (userData.language == '')
      userData.language = 'English';

    const loadedUser = new User(
      userData.email,
      userData.id,
      userData.memberId,
      userData.isLeader,
      userData.firstName,
      userData.lastName,
      userData.phoneNumber,
      userData.roles,
      userData._token,
      userData._tokenExpiration,
      userData.language
    );
    this.userData = userData;
    await this.identityService.loadUser(loadedUser);
    this.globalService.currentUser = userData.email;
    this.globalService.currentUserId = userData.id;
    this.globalService.currentUserName =
      userData.firstName + ' ' + userData.lastName;
    this.globalService.currentFirstName = userData.firstName;
    if (loadedUser.token) this.user.next(loadedUser);
    const expirationDuration =
      new Date(userData._tokenExpiration).getTime() - new Date().getTime();
    this.autoLogout(expirationDuration, mode);
    return loadedUser;
  }

  async autoLogin(mode: number): Promise<User> {
    const userData: {
      email: string;
      id: string;
      firstName: string;
      lastName: string;
      phoneNumber: string;
      roles: string[];
      memberId: number;
      isLeader: boolean;
      _token: string;
      _tokenExpiration: Date;
      language: string;
    } = this.decrypt(localStorage.getItem('userData'));
    var memberId = localStorage.getItem('memberId');  // check if old properties exist then signout
    var isLeader = localStorage.getItem('isLeader');
    if (memberId != null || isLeader != null) {
      this.logout(mode);
      return null;
    }
    if (!userData || (userData.isLeader === undefined) || (userData.memberId === undefined) || this.isOutdated(userData._token)) {
      this.logout(mode);
      return null;
    }

    if (userData.language == '')
      userData.language = 'English';

    const loadedUser = new User(
      userData.email,
      userData.id,
      userData.memberId,
      userData.isLeader,
      userData.firstName,
      userData.lastName,
      userData.phoneNumber,
      userData.roles,
      userData._token,
      userData._tokenExpiration,
      userData.language
    );
    this.userData = userData;
    await this.identityService.loadUser(loadedUser);
    this.globalService.currentUser = userData.email;
    this.globalService.currentUserId = userData.id;
    this.globalService.currentUserName =
      userData.firstName + ' ' + userData.lastName;
    this.globalService.currentFirstName = userData.firstName;
    if (loadedUser.token) this.user.next(loadedUser);
    const expirationDuration =
      new Date(userData._tokenExpiration).getTime() - new Date().getTime();
    this.autoLogout(expirationDuration, mode);
    return loadedUser;
  }

  autoLogout(expirationDuration: number, mode: number) {
    this.tokenExpirationTimer = setTimeout(() => {
      this.logout(mode);
    }, expirationDuration);
  }

  async handleAuthentication(
    email: string,
    id: string,
    memberId: number,
    isLeader: boolean,
    firstName: string,
    lastName: string,
    phoneNumber: string,
    roles: string[],
    token: string,
    expiration: Date,
    language: string
  ) {
    const user = new User(
      email,
      id,
      memberId,
      isLeader,
      firstName,
      lastName,
      phoneNumber,
      roles,
      token,
      expiration,
      language
    );
    this.user.next(user);
    let tenant = localStorage.getItem('tenant');
    var encryptedUser = this.encrypt(user);
    localStorage.setItem('userData', encryptedUser);
    if (user != null && user.id != null && user.id.length == 36) {
      localStorage.setItem('CSBarcodeID', user.id);
      this.appService.sendToNative(user.id, tenant);
    }
    await this.identityService.loadUser(user);
    this.globalService.currentUser = user.email;
    this.globalService.currentUserId = user.id;
  
    const expirationDuration =
      new Date(expiration).getTime() - new Date().getTime();
    this.autoLogout(expirationDuration, 1);
  }

  saveLogin(user: User) {
    var encryptedUser = this.encrypt(user);
    let tenant = localStorage.getItem('tenant');
    localStorage.setItem('userData', encryptedUser);
    if (user != null && user.id.length == 36) {
      localStorage.setItem('CSBarcodeID', user.id);
      this.appService.sendToNative(user.id, tenant);
    }
  }

  private handleError(errorResponse: HttpErrorResponse) {
    let errorMessage = {
      message: 'Please check your connection! ',
    };
    if (
      !errorResponse.error ||
      errorResponse.status == 0 ||
      errorResponse.status == 500
    ) {
      return throwError(errorMessage);
    } else errorMessage = errorResponse.error;
    // errorMessage = { message: errorResponse.message };
    return throwError(errorMessage);
  }

  passwordMismatchValidator(control: FormGroup): { [s: string]: boolean } {
    // Two passwords match validation
    const password = control.get('password');
    const repeatPassword = control.get('confirmPassword');
    return password.value != repeatPassword.value
      ? { passwordMismatch: true }
      : null;
  }
}
